import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios, { API } from '@/utils/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Lock, Eye, EyeOff, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

// Default neutral theme (will be replaced by client theme)
const DEFAULT_THEME = {
  primary_color: "#3B82F6",
  secondary_color: "#6366F1",
  background_primary: "#0F172A",
  background_secondary: "#1E293B",
  text_primary: "#F8FAFC",
  text_secondary: "#CBD5E1",
  border_color: "#475569"
};

export default function B2BResetPassword() {
  const { clientId, token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [theme, setTheme] = useState(DEFAULT_THEME);
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  // Fetch client info for branding
  useEffect(() => {
    const fetchClientInfo = async () => {
      try {
        const res = await axios.get(`${API}/b2b/client-info/${clientId}`);
        setClientData(res.data);
        if (res.data.theme) {
          setTheme({
            ...DEFAULT_THEME,
            ...res.data.theme
          });
        }
      } catch (err) {
        console.error('Failed to load client info:', err);
        setError('Invalid or expired reset link');
      } finally {
        setLoading(false);
      }
    };

    fetchClientInfo();
  }, [clientId]);

  // Apply CSS variables for B2B theme when theme state changes
  useEffect(() => {
    const root = document.documentElement;
    
    // Set CSS variables for global styling (including toasts)
    root.style.setProperty('--theme-primary', theme.primary_color);
    root.style.setProperty('--theme-accent', theme.secondary_color || theme.primary_color);
    root.style.setProperty('--theme-card', theme.background_secondary);
    root.style.setProperty('--theme-text', theme.text_primary);
    root.style.setProperty('--theme-border', theme.border_color);
    
    // Calculate button text color based on primary color luminance
    const hex = theme.primary_color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    root.style.setProperty('--theme-button-text', luminance > 0.5 ? '#1a1a2e' : '#ffffff');
    
    // Cleanup on unmount - reset to defaults
    return () => {
      root.style.removeProperty('--theme-primary');
      root.style.removeProperty('--theme-accent');
      root.style.removeProperty('--theme-card');
      root.style.removeProperty('--theme-text');
      root.style.removeProperty('--theme-border');
      root.style.removeProperty('--theme-button-text');
    };
  }, [theme]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.password || !formData.confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    try {
      setSubmitting(true);
      await axios.post(`${API}/b2b/reset-password`, {
        token: token,
        client_id: clientId,
        new_password: formData.password
      });
      
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to reset password. Link may be expired.';
      toast.error(message);
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: theme.background_primary }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: theme.primary_color }} />
      </div>
    );
  }

  if (error && !success) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{ 
          background: theme.background_primary // SOLID color for B2B
        }}
      >
        <div 
          className="w-full max-w-md p-8 rounded-2xl shadow-2xl text-center"
          style={{ 
            background: theme.background_secondary,
            border: `1px solid ${theme.border_color}`
          }}
        >
          <AlertCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#ef4444' }} />
          <h1 className="text-2xl font-bold mb-4" style={{ color: theme.text_primary }}>Invalid Reset Link</h1>
          <p style={{ color: theme.text_secondary }} className="mb-6">
            {error}
          </p>
          <Button
            onClick={() => navigate(`/portal/${clientId}/forgot-password`)}
            className="w-full h-12"
            style={{
              background: theme.primary_color // SOLID color for B2B
            }}
          >
            Request New Reset Link
          </Button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{ 
          background: theme.background_primary // SOLID color for B2B
        }}
      >
        <div 
          className="w-full max-w-md p-8 rounded-2xl shadow-2xl text-center"
          style={{ 
            background: theme.background_secondary,
            border: `1px solid ${theme.border_color}`
          }}
        >
          <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#22c55e' }} />
          <h1 className="text-2xl font-bold mb-4" style={{ color: theme.text_primary }}>Password Reset!</h1>
          <p style={{ color: theme.text_secondary }} className="mb-6">
            Your password has been successfully reset. You can now log in with your new password.
          </p>
          <Button
            onClick={() => navigate(`/portal/${clientId}/login`)}
            className="w-full h-12"
            style={{
              background: theme.primary_color // SOLID color for B2B
            }}
          >
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ 
        background: theme.background_primary // SOLID color for B2B
      }}
    >
      <div 
        className="w-full max-w-md p-8 rounded-2xl shadow-2xl"
        style={{ 
          background: theme.background_secondary,
          border: `1px solid ${theme.border_color}`
        }}
      >
        {/* Header */}
        <div className="text-center mb-8">
          {clientData?.logo_url ? (
            <img 
              src={clientData.logo_url} 
              alt={clientData.app_name || 'Logo'} 
              className="h-16 mx-auto mb-4 object-contain"
            />
          ) : (
            <h1 
              className="text-2xl font-bold mb-2"
              style={{ color: theme.primary_color }}
            >
              {clientData?.app_name || 'Reset Password'}
            </h1>
          )}
          <p style={{ color: theme.text_secondary }}>
            Enter your new password below
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <Lock 
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" 
              style={{ color: theme.text_secondary }}
            />
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="New Password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="pl-10 pr-10 h-12"
              style={{
                background: theme.background_primary,
                borderColor: theme.border_color,
                color: theme.text_primary
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2"
              style={{ color: theme.text_secondary }}
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          <div className="relative">
            <Lock 
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" 
              style={{ color: theme.text_secondary }}
            />
            <Input
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm New Password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              className="pl-10 pr-10 h-12"
              style={{
                background: theme.background_primary,
                borderColor: theme.border_color,
                color: theme.text_primary
              }}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2"
              style={{ color: theme.text_secondary }}
            >
              {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-12 text-white font-semibold"
            style={{
              background: theme.primary_color // SOLID color for B2B
            }}
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Resetting...
              </>
            ) : (
              'Reset Password'
            )}
          </Button>
        </form>

        {/* Back to login link */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate(`/portal/${clientId}/login`)}
            className="text-sm hover:underline"
            style={{ color: theme.text_secondary }}
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
}
